import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Inbox, Phone, Mail, MapPin, Clock, MessageCircle, CheckCircle2, Crown, Building2, User, Download, Search, Filter, RefreshCw, Bell } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { API } from '../../utils/api';
import { authAxios } from '../../utils/authAxios';

var POLL_INTERVAL_MS = 30000;
var NOTIFY_SOUND_URL = 'https://cdn.pixabay.com/audio/2022/03/10/audio_27be5f2d9e.mp3';

export default function AdminQuoteRequests() {
  var [items, setItems] = useState([]);
  var [loading, setLoading] = useState(true);
  var [refreshing, setRefreshing] = useState(false);
  var [lastCheck, setLastCheck] = useState(null);
  var [search, setSearch] = useState('');
  var [filterTarget, setFilterTarget] = useState('all'); // all | center | professional
  var [filterUrgency, setFilterUrgency] = useState('all');
  var [expanded, setExpanded] = useState(null);
  var knownIdsRef = useRef(new Set());
  var audioRef = useRef(null);
  var soundEnabledRef = useRef(true);
  var isFirstLoadRef = useRef(true);
  var [soundOn, setSoundOn] = useState(true);

  useEffect(function() {
    try {
      audioRef.current = new Audio(NOTIFY_SOUND_URL);
      audioRef.current.volume = 0.4;
    } catch (e) {}
    try {
      var pref = localStorage.getItem('admin_quotes_sound');
      if (pref === '0') { soundEnabledRef.current = false; setSoundOn(false); }
    } catch (e) {}
    load(true);
    var poll = setInterval(function() {
      if (typeof document !== 'undefined' && document.visibilityState === 'visible') load(false);
    }, POLL_INTERVAL_MS);
    function onVisibility() { if (document.visibilityState === 'visible') load(false); }
    if (typeof document !== 'undefined') document.addEventListener('visibilitychange', onVisibility);
    return function() {
      clearInterval(poll);
      if (typeof document !== 'undefined') document.removeEventListener('visibilitychange', onVisibility);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function load(initial) {
    if (initial) setLoading(true); else setRefreshing(true);
    authAxios.get(API + '/admin/quote-requests?limit=500')
      .then(function(res) {
        var list = res.data.quote_requests || [];
        var currentIds = new Set(list.map(function(q) { return q.quote_id; }));
        var newOnes = [];
        if (!isFirstLoadRef.current) {
          list.forEach(function(q) {
            if (q.quote_id && !knownIdsRef.current.has(q.quote_id)) newOnes.push(q);
          });
        }
        knownIdsRef.current = currentIds;
        setItems(list);
        setLastCheck(new Date());
        if (newOnes.length > 0) {
          if (soundEnabledRef.current && audioRef.current) {
            try { audioRef.current.currentTime = 0; audioRef.current.play().catch(function() {}); } catch (e) {}
          }
          var first = newOnes[0] || {};
          var label = newOnes.length === 1
            ? 'Nueva cotización: ' + (first.name || 'paciente')
            : newOnes.length + ' nuevas cotizaciones recibidas';
          try {
            toast.success(label, {
              description: first.region ? 'Región: ' + first.region + ' · ' + (first.target_type === 'professional' ? 'Profesional' : 'Centro') : undefined,
              duration: 6000
            });
          } catch (e) {}
          try {
            if (typeof window !== 'undefined' && 'Notification' in window && Notification.permission === 'granted') {
              new Notification(label, { body: first.region || '', icon: '/favicon.ico', tag: 'adm-q-' + (first.quote_id || 'n') });
            }
          } catch (e) {}
        }
        isFirstLoadRef.current = false;
      })
      .catch(function() { /* keep previous items */ })
      .finally(function() { if (initial) setLoading(false); else setRefreshing(false); });
  }

  function toggleSound() {
    var next = !soundEnabledRef.current;
    soundEnabledRef.current = next;
    setSoundOn(next);
    try { localStorage.setItem('admin_quotes_sound', next ? '1' : '0'); } catch (e) {}
    try {
      if (next && typeof window !== 'undefined' && 'Notification' in window && Notification.permission === 'default') {
        Notification.requestPermission().catch(function() {});
      }
    } catch (e) {}
  }

  var filtered = items.filter(function(q) {
    if (filterTarget !== 'all' && q.target_type !== filterTarget) return false;
    if (filterUrgency !== 'all' && q.urgency !== filterUrgency) return false;
    if (search) {
      var s = search.toLowerCase();
      if (!(q.name || '').toLowerCase().includes(s) &&
          !(q.email || '').toLowerCase().includes(s) &&
          !(q.phone || '').toLowerCase().includes(s) &&
          !(q.region || '').toLowerCase().includes(s) &&
          !(q.modality || '').toLowerCase().includes(s)) return false;
    }
    return true;
  });

  // Stats
  var totalCenters = items.filter(function(q) { return q.target_type === 'center'; }).length;
  var totalPros = items.filter(function(q) { return q.target_type === 'professional'; }).length;
  var urgent = items.filter(function(q) { return q.urgency === 'inmediata'; }).length;
  var totalMatches = items.reduce(function(sum, q) { return sum + (q.matched_count || 0); }, 0);
  var last24h = items.filter(function(q) {
    if (!q.created_at) return false;
    return (Date.now() - new Date(q.created_at).getTime()) < 86400000;
  }).length;
  // Helper to resolve origin from quote: prefer structured field, fallback to legacy message regex
  function resolveOrigin(q) {
    var raw = (q.source || '').trim();
    if (!raw) {
      var msg = q.message || '';
      var legacy = msg.match(/\[Origen:\s*([^\]]+)\]/);
      raw = legacy ? legacy[1].trim() : '';
    }
    if (!raw) return 'Desconocido';
    var base = raw.split('@')[0];
    var suffix = raw.indexOf('@') !== -1 ? raw.split('@').slice(1).join('@') : '';
    var label = base;
    if (base === 'cotizar-landing') label = 'Landing /cotizar';
    else if (base === 'floating-widget') label = 'Widget flotante';
    else if (base === 'center-detail') label = 'Ficha de centro';
    else if (base.indexOf('satellite-') === 0) label = 'Satélite (' + base.replace('satellite-', '') + ')';
    return suffix ? (label + ' · ' + suffix) : label;
  }

  var bySource = {};
  items.forEach(function(q) {
    var origin = resolveOrigin(q);
    bySource[origin] = (bySource[origin] || 0) + 1;
  });

  function formatDate(iso) {
    if (!iso) return '';
    var d = new Date(iso);
    return d.toLocaleString('es-CL', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
  }

  function urgencyBadge(u) {
    if (!u) return null;
    var map = {
      inmediata: { label: 'Urgente', cls: 'bg-red-500/20 text-red-300 border-red-500/40' },
      esta_semana: { label: 'Esta semana', cls: 'bg-amber-500/20 text-amber-300 border-amber-500/40' },
      flexible: { label: 'Flexible', cls: 'bg-slate-500/20 text-slate-300 border-slate-500/40' }
    };
    var info = map[u] || { label: u, cls: 'bg-slate-500/20 text-slate-300 border-slate-500/40' };
    return <span className={'px-2 py-0.5 text-xs rounded-full border ' + info.cls}>{info.label}</span>;
  }

  function exportCSV() {
    var rows = [['Fecha', 'Nombre', 'Teléfono', 'Email', 'Tipo', 'Región', 'Modalidad', 'Adicción', 'Urgencia', 'Matched', 'Origen', 'Mensaje']];
    filtered.forEach(function(q) {
      rows.push([
        q.created_at || '', q.name || '', q.phone || '', q.email || '',
        q.target_type || '', q.region || '', q.modality || '', q.addiction || '',
        q.urgency || '', q.matched_count || 0, resolveOrigin(q), (q.message || '').replace(/\n/g, ' ').replace(/"/g, '""')
      ]);
    });
    var csv = rows.map(function(r) {
      return r.map(function(c) { return '"' + String(c) + '"'; }).join(',');
    }).join('\n');
    var blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
    var url = URL.createObjectURL(blob);
    var a = document.createElement('a');
    a.href = url;
    a.download = 'cotizaciones_' + new Date().toISOString().slice(0, 10) + '.csv';
    a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div className="space-y-6" data-testid="admin-quotes">
      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Card className="bg-slate-900 border-slate-800"><CardContent className="p-4"><div className="flex items-center gap-2 text-slate-400 text-xs"><Inbox className="w-3.5 h-3.5" />Total</div><div className="text-2xl font-bold text-slate-50 mt-1" data-testid="stat-total">{items.length}</div></CardContent></Card>
        <Card className="bg-slate-900 border-slate-800"><CardContent className="p-4"><div className="flex items-center gap-2 text-slate-400 text-xs"><Clock className="w-3.5 h-3.5" />Últimas 24h</div><div className="text-2xl font-bold text-emerald-400 mt-1">{last24h}</div></CardContent></Card>
        <Card className="bg-slate-900 border-slate-800"><CardContent className="p-4"><div className="flex items-center gap-2 text-slate-400 text-xs"><Building2 className="w-3.5 h-3.5" />Para centros</div><div className="text-2xl font-bold text-blue-400 mt-1">{totalCenters}</div></CardContent></Card>
        <Card className="bg-slate-900 border-slate-800"><CardContent className="p-4"><div className="flex items-center gap-2 text-slate-400 text-xs"><User className="w-3.5 h-3.5" />Para pros</div><div className="text-2xl font-bold text-purple-400 mt-1">{totalPros}</div></CardContent></Card>
        <Card className="bg-slate-900 border-slate-800"><CardContent className="p-4"><div className="flex items-center gap-2 text-slate-400 text-xs">Total matches</div><div className="text-2xl font-bold text-amber-400 mt-1">{totalMatches}</div></CardContent></Card>
      </div>

      {/* Origen */}
      {Object.keys(bySource).length > 0 && (
        <Card className="bg-slate-900 border-slate-800">
          <CardContent className="p-4">
            <div className="text-xs font-semibold text-slate-400 mb-3 flex items-center gap-2"><Filter className="w-3.5 h-3.5" />Origen de las cotizaciones</div>
            <div className="flex flex-wrap gap-2">
              {Object.keys(bySource).sort(function(a, b) { return bySource[b] - bySource[a]; }).map(function(src) {
                return <span key={src} className="px-3 py-1.5 bg-slate-800 rounded-lg text-xs text-slate-200 border border-slate-700"><strong className="text-emerald-400">{bySource[src]}</strong> · {src}</span>;
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <Card className="bg-slate-900 border-slate-800">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
              <Input value={search} onChange={function(e) { setSearch(e.target.value); }} placeholder="Buscar por nombre, email, región…" data-testid="quotes-search" className="pl-9 bg-slate-950 border-slate-700 text-slate-100" />
            </div>
            <select value={filterTarget} onChange={function(e) { setFilterTarget(e.target.value); }} data-testid="quotes-filter-target" className="bg-slate-950 border border-slate-700 rounded-md px-3 py-2 text-sm text-slate-100">
              <option value="all">Todos los tipos</option>
              <option value="center">Para centros</option>
              <option value="professional">Para profesionales</option>
            </select>
            <select value={filterUrgency} onChange={function(e) { setFilterUrgency(e.target.value); }} className="bg-slate-950 border border-slate-700 rounded-md px-3 py-2 text-sm text-slate-100">
              <option value="all">Toda urgencia</option>
              <option value="inmediata">Inmediata</option>
              <option value="esta_semana">Esta semana</option>
              <option value="flexible">Flexible</option>
            </select>
            <Button onClick={exportCSV} variant="outline" size="sm" className="border-emerald-600 text-emerald-400 hover:bg-emerald-500/10" data-testid="quotes-export-csv">
              <Download className="w-4 h-4 mr-1.5" />CSV
            </Button>
            <button
              onClick={function() { load(false); }}
              disabled={refreshing}
              data-testid="admin-quotes-refresh"
              title={lastCheck ? 'Última actualización: ' + lastCheck.toLocaleTimeString('es-CL', { hour: '2-digit', minute: '2-digit', second: '2-digit' }) : 'Refrescar'}
              className="p-2 rounded-md text-slate-400 hover:text-emerald-400 hover:bg-slate-800 transition disabled:opacity-50"
              aria-label="Refrescar"
            >
              <RefreshCw className={'w-4 h-4 ' + (refreshing ? 'animate-spin text-emerald-400' : '')} />
            </button>
            <button
              onClick={toggleSound}
              data-testid="admin-quotes-sound-toggle"
              title={soundOn ? 'Desactivar avisos sonoros' : 'Activar avisos sonoros'}
              className={'p-2 rounded-md transition ' + (soundOn ? 'text-emerald-400 hover:bg-slate-800' : 'text-slate-600 hover:text-slate-400 hover:bg-slate-800')}
              aria-label="Avisos sonoros"
            >
              <Bell className="w-4 h-4" />
            </button>
          </div>
          <p className="text-xs text-slate-500 mt-2">Mostrando <strong className="text-slate-300">{filtered.length}</strong> de {items.length}{urgent > 0 && <> · <span className="text-red-400">{urgent} urgentes</span></>}{lastCheck && <> · <span className="text-slate-400">Actualizado: {lastCheck.toLocaleTimeString('es-CL', { hour: '2-digit', minute: '2-digit' })}</span></>}</p>
        </CardContent>
      </Card>

      {/* List */}
      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-emerald-500" /></div>
      ) : filtered.length === 0 ? (
        <Card className="bg-slate-900 border-slate-800"><CardContent className="p-10 text-center"><Inbox className="w-12 h-12 text-slate-700 mx-auto mb-3" /><p className="text-slate-400">No hay cotizaciones que coincidan con los filtros.</p></CardContent></Card>
      ) : (
        <div className="space-y-2">
          {filtered.map(function(q) {
            var isExp = expanded === q.quote_id;
            var origin = resolveOrigin(q);
            var msg = q.message || '';
            var cleanMsg = msg.replace(/\[Origen:[^\]]+\]/, '').trim();
            return (
              <Card key={q.quote_id} data-testid={'admin-quote-' + q.quote_id} className="bg-slate-900 border-slate-800 hover:border-slate-700 cursor-pointer" onClick={function() { setExpanded(isExp ? null : q.quote_id); }}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        {q.target_type === 'center' ? <Building2 className="w-4 h-4 text-blue-400 flex-shrink-0" /> : <User className="w-4 h-4 text-purple-400 flex-shrink-0" />}
                        <h4 className="font-semibold text-slate-50">{q.name}</h4>
                        {urgencyBadge(q.urgency)}
                        <span className="text-xs text-slate-500">· matched: <strong className="text-amber-400">{q.matched_count || 0}</strong></span>
                      </div>
                      <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-slate-400 mt-1">
                        <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{q.region || '—'}</span>
                        {q.modality && <span>· {q.modality}</span>}
                        {q.addiction && <span className="text-emerald-400">· {q.addiction}</span>}
                        <span>· <span className="text-blue-300">{origin}</span></span>
                      </div>
                    </div>
                    <div className="text-xs text-slate-500 text-right flex-shrink-0">
                      <Clock className="w-3 h-3 inline mr-1" />{formatDate(q.created_at)}
                    </div>
                  </div>

                  {isExp && (
                    <div className="mt-4 pt-4 border-t border-slate-800 grid md:grid-cols-2 gap-3 text-sm">
                      <div className="bg-slate-800/40 rounded p-3">
                        <p className="text-slate-500 text-xs mb-1">Teléfono</p>
                        <a href={'https://wa.me/' + (q.phone || '').replace(/[^0-9]/g, '')} target="_blank" rel="noopener noreferrer" onClick={function(e) { e.stopPropagation(); }} className="text-emerald-400 font-medium flex items-center gap-1.5"><MessageCircle className="w-3.5 h-3.5" />{q.phone}</a>
                      </div>
                      {q.email && (
                        <div className="bg-slate-800/40 rounded p-3">
                          <p className="text-slate-500 text-xs mb-1">Email</p>
                          <a href={'mailto:' + q.email} onClick={function(e) { e.stopPropagation(); }} className="text-blue-400 font-medium break-all">{q.email}</a>
                        </div>
                      )}
                      {cleanMsg && (
                        <div className="md:col-span-2 bg-slate-800/40 rounded p-3 border-l-2 border-emerald-500">
                          <p className="text-slate-500 text-xs mb-1">Mensaje del usuario</p>
                          <p className="text-slate-300 italic">"{cleanMsg}"</p>
                        </div>
                      )}
                      <div className="md:col-span-2 text-xs text-slate-600">
                        ID: <code className="text-slate-500">{q.quote_id}</code>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
